import React, { useState, useEffect } from 'react';
import { 
  Ticket, Clock, Calendar, Crown, Users, Lock,
  Sparkles, ChevronRight
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════════
   TICKETED SHOW CARD — Premium Golden VIP Design
   
   Visual style:
   - Rich gold/metallic gold dominant color
   - Embossed texture effect
   - Hourglass countdown metaphor
   - VIP badge
   - Strong CTA button
═══════════════════════════════════════════════════════════════════════════════ */

const formatEuro = (n) => `${(n || 0).toFixed(2)}€`;

const CountdownTimer = ({ timeUntil }) => {
  const [time, setTime] = useState(timeUntil);
  
  useEffect(() => {
    if (!timeUntil) return;
    setTime(timeUntil);
    
    const interval = setInterval(() => {
      setTime(prev => {
        if (!prev || prev.total_seconds <= 0) return prev;
        const newTotal = prev.total_seconds - 1;
        return {
          total_seconds: newTotal,
          days: Math.floor(newTotal / 86400),
          hours: Math.floor((newTotal % 86400) / 3600),
          minutes: Math.floor((newTotal % 3600) / 60),
          seconds: newTotal % 60
        };
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [timeUntil]);
  
  if (!time || time.total_seconds <= 0) {
    return <span className="text-green-400 font-bold animate-pulse">¡EN VIVO AHORA!</span>;
  }
  
  if (time.days > 0) {
    return (
      <span className="font-mono">
        {time.days}d {time.hours}h {time.minutes}m
      </span>
    );
  }
  
  return (
    <span className="font-mono">
      {String(time.hours).padStart(2, '0')}:{String(time.minutes).padStart(2, '0')}:{String(time.seconds || 0).padStart(2, '0')}
    </span>
  );
};

export const TicketedShowCard = ({ 
  show, 
  onPurchase, 
  onView,
  userHasTicket = false,
  lang = 'es',
  featured = false 
}) => {
  const scheduledDate = new Date(show.scheduled_at);
  const formattedDate = scheduledDate.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
  const formattedTime = scheduledDate.toLocaleTimeString(lang === 'es' ? 'es-ES' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div 
      data-testid={`ticketed-show-card-${show.id}`}
      className={`group relative overflow-hidden rounded-2xl transition-all duration-500 hover:scale-[1.02] ${
        featured ? 'col-span-2' : ''
      }`}
      style={{
        background: 'linear-gradient(145deg, #2a2005 0%, #1a1303 50%, #0d0a02 100%)',
      }}
    >
      {/* Metallic texture overlay */}
      <div 
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
      
      {/* Golden glow border */}
      <div className="absolute inset-0 rounded-2xl border-2 border-[#d4a853]/40 group-hover:border-[#d4a853]/60 transition-colors" />
      <div className="absolute inset-0 rounded-2xl shadow-[inset_0_1px_1px_rgba(212,168,83,0.3),inset_0_-1px_1px_rgba(0,0,0,0.5)]" />
      
      {/* Content */}
      <div className="relative z-10 p-5">
        {/* Header: VIP Badge + Price */}
        <div className="flex items-start justify-between mb-4">
          <div 
            data-testid="vip-badge"
            className="flex items-center gap-1.5 px-3 py-1 rounded-full"
            style={{
              background: 'linear-gradient(135deg, #d4a853 0%, #b8942a 100%)',
              boxShadow: '0 2px 8px rgba(212,168,83,0.4)'
            }}
          >
            <Crown size={12} className="text-black" />
            <span className="text-[10px] font-bold text-black uppercase tracking-wider">VIP</span>
          </div>
          
          <div className="text-right">
            <span className="text-2xl font-bold text-[#d4a853]">{formatEuro(show.price)}</span>
          </div>
        </div>
        
        {/* Banner Image */}
        {show.banner_url && (
          <div className="relative mb-4 rounded-xl overflow-hidden aspect-video">
            <img 
              src={show.banner_url} 
              alt={show.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        )}
        
        {/* Title */}
        <h3 
          data-testid="show-title"
          className="text-xl font-bold text-white mb-2 line-clamp-2"
          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
        >
          {show.title}
        </h3>
        
        {/* Artist */}
        <p className="text-sm text-[#d4a853]/80 mb-4 flex items-center gap-2">
          <Sparkles size={14} />
          {show.artist_name}
        </p>
        
        {/* Description */}
        <p className="text-sm text-white/60 mb-4 line-clamp-2">
          {show.description}
        </p>
        
        {/* Date & Time */}
        <div className="flex items-center gap-4 mb-4 text-sm">
          <div className="flex items-center gap-1.5 text-white/70">
            <Calendar size={14} className="text-[#d4a853]" />
            <span className="capitalize">{formattedDate}</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/70">
            <Clock size={14} className="text-[#d4a853]" />
            <span>{formattedTime}</span>
          </div>
        </div>
        
        {/* Countdown */}
        <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-black/40 backdrop-blur-sm border border-[#d4a853]/20 mb-4">
          <span className="text-xs text-white/50 uppercase tracking-wider">
            {lang === 'es' ? 'Comienza en' : 'Starts in'}
          </span>
          <div className="text-lg font-bold text-[#d4a853]">
            <CountdownTimer timeUntil={show.time_until} />
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex items-center gap-4 mb-4 text-xs text-white/50">
          <span className="flex items-center gap-1">
            <Ticket size={12} className="text-[#d4a853]" />
            {show.tickets_sold || 0} {lang === 'es' ? 'tickets vendidos' : 'tickets sold'}
          </span>
        </div>
        
        {/* CTA Button */}
        {userHasTicket ? (
          <button
            data-testid="view-show-btn"
            onClick={() => onView?.(show)}
            className="w-full py-3 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              boxShadow: '0 4px 15px rgba(34,197,94,0.4)'
            }}
          >
            <Lock size={16} className="text-white" />
            {lang === 'es' ? 'Acceso Desbloqueado' : 'Access Unlocked'}
            <ChevronRight size={16} />
          </button>
        ) : (
          <button
            data-testid="purchase-ticket-btn"
            onClick={() => onPurchase?.(show)}
            className="w-full py-3 rounded-xl font-bold text-sm text-black transition-all duration-300 hover:brightness-110 flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #d4a853 0%, #b8942a 100%)',
              boxShadow: '0 4px 15px rgba(212,168,83,0.4)'
            }}
          >
            <Ticket size={16} />
            {lang === 'es' ? 'Adquirir Ticket' : 'Get Ticket'}
            <ChevronRight size={16} />
          </button>
        )}
      </div>
      
      {/* Animated gold particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#d4a853] rounded-full opacity-40 animate-float-particle"
            style={{
              left: `${20 + i * 15}%`,
              top: `${10 + (i % 3) * 30}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + i}s`
            }}
          />
        ))}
      </div>
      
      {/* CSS */}
      <style>{`
        @keyframes float-particle {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.2; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 0.6; }
        }
        .animate-float-particle {
          animation: float-particle 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   TICKETED SHOWS SECTION — For Live Lobby
═══════════════════════════════════════════════════════════════════════════════ */

export const TicketedShowsSection = ({ 
  shows = [], 
  onPurchase, 
  onView,
  userTickets = [],
  lang = 'es' 
}) => {
  if (shows.length === 0) return null;
  
  const userTicketShowIds = new Set(userTickets.map(t => t.show_id));
  
  return (
    <section data-testid="ticketed-shows-section" className="mb-8">
      {/* Section Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#d4a853]/20 flex items-center justify-center">
          <Crown size={20} className="text-[#d4a853]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            {lang === 'es' ? 'Shows Programados' : 'Upcoming Shows'}
            <span className="px-2 py-0.5 text-xs rounded-full bg-[#d4a853]/20 text-[#d4a853] font-medium">
              VIP
            </span>
          </h2>
          <p className="text-sm text-white/50">
            {lang === 'es' ? 'Eventos exclusivos con acceso por ticket' : 'Exclusive ticketed events'}
          </p>
        </div>
      </div>
      
      {/* Shows Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shows.map((show, i) => (
          <TicketedShowCard
            key={show.id}
            show={show}
            onPurchase={onPurchase}
            onView={onView}
            userHasTicket={userTicketShowIds.has(show.id)}
            lang={lang}
            featured={i === 0 && shows.length > 2}
          />
        ))}
      </div>
    </section>
  );
};
